import React, { useCallback, useContext, useEffect, useState } from 'react';
import Drawer from '@src/components/wysiwyg_editor/toolbox/Drawer';
import Header from '@src/components/wysiwyg_editor/toolbox/drawer_headers/Text';
import {
  ImageCacheDispatchContext,
  ImageData,
  ImageGroup,
  ImageCacheStateContext
} from '@src/contexts/wysiwyg/image_cache';
import arrowUp from '@images/wysiwyg_editor/image_cache/arrow_up.svg';

import AddPhotos from '../buttons/AddPhotos';
import useIsLargeScreen from '@src/hooks/useIsLargeScreen';
import { WysiwygEditorDispatchContext } from '@src/contexts/wysiwyg/editor';
import useSetImageContent from '@src/hooks/wysiwyg_editor/useSetImageContent';
import {
  PageContentDispatchContext,
  PageContentStateContext
} from '@src/contexts/wysiwyg/page_content';
import { useImageGenerator } from '@src/hooks/wysiwyg_editor/useImageGenerator';
import { useDispatch } from 'react-redux';
import { addPageContentItem, saveSnapshot } from '@src/actions/wysiwyg/editor';
import { isImage } from '@src/utils/wysiwyg_editor/data';
import classNames from 'classnames';
import loadingSpinner from '@images/spinners/double-ring-200px.gif';
import { isIE } from 'react-device-detect';

type ImageRows = Array<ImageGroup>;

function applyImageWidths(rows: ImageRows) {
  rows.forEach((row) => {
    const widthsAt100Height = row.map(
      (imageData) =>
        (imageData.naturalDimensions.width * 100) /
        imageData.naturalDimensions.height
    );

    const totalWidth = widthsAt100Height.reduce((all, width) => all + width, 0);

    widthsAt100Height.forEach((width, index) => {
      row[index].widthPercentage = (100 * width) / totalWidth;
    });
  });
}

function doesImageFitInRow(row: ImageRows, _image: ImageData): boolean {
  if (row.length < 2) {
    return true;
  } else {
    // check image height/width ratio
    // ideas:
    // - (> single image 1.5 width ratio goes on a single row?)
    // - (> 2 images and either is > 1.5 width ratio goes on separate rows?)
    // what else?
  }

  // if all else fails, just start a new row
  return false;
}

function SelectImagesMessage() {
  return (
    <div className="wysiwyg-editor-drawer-photos-none">
      <img src={arrowUp as string} />
      <p>Select multiple photos to add onto your page</p>
    </div>
  );
}

function MobileImage({ image, index }: { image: ImageData; index: number }) {
  const dispatch = useDispatch();
  const imageGenerator = useImageGenerator();
  const { closeToolbox } = useContext(WysiwygEditorDispatchContext);
  const { clearInsertionTarget } = useContext(ImageCacheDispatchContext);
  const setImageContent = useSetImageContent();
  const { insertionTarget } = useContext(ImageCacheStateContext);
  const { getItemById } = useContext(PageContentDispatchContext);
  const pageItem = getItemById(insertionTarget);

  const addImageToPage = useCallback(() => {
    const newImageItem = imageGenerator({
      url: image.url,
      imageDimensions: image.naturalDimensions,
      centered: true
    });

    dispatch(saveSnapshot());
    dispatch(addPageContentItem(newImageItem));
  }, [dispatch, image.naturalDimensions, image.url, imageGenerator]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (isImage(pageItem)) {
        setImageContent(pageItem, image);
        clearInsertionTarget();
      } else {
        addImageToPage();
      }

      closeToolbox();
    },
    [
      addImageToPage,
      clearInsertionTarget,
      closeToolbox,
      image,
      pageItem,
      setImageContent
    ]
  );

  return (
    <img
      src={image.url}
      alt=""
      key={`image-${index}`}
      className="selected"
      onClick={handleClick}
    />
  );
}

function DesktopImage({ image, index }: { image: ImageData; index: number }) {
  const { startDragging, stopDragging } = useContext(ImageCacheDispatchContext);
  const { contentItems } = useContext(PageContentStateContext);
  const setImageContent = useSetImageContent();

  const placeInFirstEmptyFrame = useCallback(() => {
    const emptyFrame = contentItems
      .filter(isImage)
      .find((item) => !item.href);

    if (emptyFrame) setImageContent(emptyFrame, image);
  }, [contentItems, image, setImageContent]);

  return (
    <img
      src={image.url}
      alt=""
      key={`image-${index}`}
      style={{ cursor: 'pointer' }}
      onClick={placeInFirstEmptyFrame}
      onDragStart={(e: React.DragEvent<HTMLImageElement>) => {
        const imageRect = (e.target as HTMLImageElement).getBoundingClientRect();

        const dragX = (e.clientX - imageRect.left) / imageRect.width;
        const dragY = (e.clientY - imageRect.top) / imageRect.height;

        // We originally used 'application/json' but Internet Explorer only
        // allows using the key "Text".
        e.dataTransfer.setData(
          'Text',
          JSON.stringify({
            data: image,
            dragOffset: { x: dragX, y: dragY }
          })
        );

        if (!isIE) e.dataTransfer.dropEffect = 'copy';

        setTimeout(() => startDragging(), 0);
      }}
      onDragEnd={() => {
        stopDragging();
      }}
      draggable
    />
  );
}

function ImageRow({
  images,
  rowNumber,
  usedImages
}: {
  images: ImageGroup;
  rowNumber: number;
  usedImages: Array<string>;
}) {
  const isLarge = useIsLargeScreen();

  return (
    <div className="wysiwyg-editor-drawer-photos-row">
      {images.map((image, imageIndex) => {
        return (
          <div
            className={classNames('wysiwyg-editor-drawer-photos-image', {
              'wysiwyg-editor-drawer-photos-image--selected': usedImages.includes(
                image.url
              )
            })}
            style={{
              width: `calc((100% - ${images.length - 1}rem) * ${
                image.widthPercentage / 100
              }`
            }}
            key={`row-${rowNumber}-image-${imageIndex}`}
          >
            {isLarge ? (
              <DesktopImage
                image={image}
                index={imageIndex}
                key={`${image.url}-${imageIndex}`}
              />
            ) : (
              <MobileImage
                image={image}
                index={imageIndex}
                key={`${image.url}-${imageIndex}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Photos(): React.ReactElement {
  const [imageRows, setImageRows] = useState<ImageRows>([]);
  const { images, isLoadingImages } = useContext(ImageCacheStateContext);
  const { contentItems } = useContext(PageContentStateContext);

  const imageUrls = contentItems
    .filter(isImage)
    .map((item) => item.href)
    .filter((href) => href);

  useEffect(() => {
    let currentRow = [];
    const allRows = [];

    images.forEach((image) => {
      if (doesImageFitInRow(currentRow, image)) {
        currentRow.push(image);
      } else {
        allRows.push(currentRow);
        currentRow = [image];
      }
    });

    if (currentRow.length > 0) allRows.push(currentRow);
    applyImageWidths(allRows);
    setImageRows(allRows);
  }, [images]);

  return (
    <Drawer className="wysiwyg-editor-toolbox-drawer-text" header={Header}>
      <AddPhotos />

      {isLoadingImages ? (
        <img src={loadingSpinner as string} alt="Loading images" />
      ) : (
        <div className="wysiwyg-editor-drawer-photos">
          {imageRows.length ? (
            imageRows.map((row, rowIndex) => (
              <ImageRow
                images={row}
                rowNumber={rowIndex}
                key={rowIndex}
                usedImages={imageUrls}
              />
            ))
          ) : (
            <SelectImagesMessage />
          )}
        </div>
      )}
    </Drawer>
  );
}

export default Photos;
